import DOMPurify from 'dompurify';
import { t } from 'i18next';
import request from '@Utils/request';

const getBlockedImagePlaceholder = () => {
  return `<div style="border: 1px dashed #ccc; padding: 8px; margin: 4px auto; color: #666; font-size: 12px; text-align: center; text-decoration: none; font-family: sans-serif; font-weight: normal; display: inline-block; width: calc(100% - 16px);">${t('common.blockedImage')}</div>`;
};

export interface ImageProxyConfig {
  /** Base URL for the image proxy endpoint */
  baseUrl: string;
  /** Whether to enable image proxying (default: false) */
  enabled?: boolean;
}

export interface ImageFetchResult {
  originalSrc: string;
  blobUrl: string;
  placeholder: string;
}

/**
 * Fetches an image through the proxy with proper authentication headers
 * @param imageUrl - Original image URL to fetch
 * @param proxyBaseUrl - Base URL for the image proxy
 * @returns Promise resolving to base64 data URL or null if failed
 */
export const fetchImageThroughProxy = async (
  imageUrl: string,
  proxyBaseUrl: string
): Promise<string | null> => {
  try {
    const response = await request({
      method: 'get',
      url: `${proxyBaseUrl}?imageUrl=${encodeURIComponent(imageUrl)}`,
      responseType: 'blob',
    });

    // Handle the axios response based on what the interceptor returns
    let blob: Blob;
    if (response instanceof Blob) {
      blob = response;
    } else if (response?.data instanceof Blob) {
      blob = response.data;
    } else {
      return null;
    }

    // Convert blob to base64 data URL
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch image through proxy:', imageUrl, error);
    return null;
  }
};

/**
 * React Query key factory for image proxy queries
 */
export const imageProxyQueryKeys = {
  image: (proxyBaseUrl: string, imageUrl: string) =>
    ['image-proxy', proxyBaseUrl, imageUrl] as const,
};

/**
 * Detects if an image is likely a tracking pixel or hidden image
 * @param imgTag - The full img tag
 * @param src - The image source URL
 * @returns true if the image appears to be a tracking pixel
 */
export const isLikelyTrackingPixel = (imgTag: string, src: string): boolean => {
  // Check for 1x1 pixel dimensions
  const has1x1Dimensions =
    /(?:width|height)=["']?1["']?/gi.test(imgTag) ||
    /(?:width|height):\s*1px/gi.test(imgTag);

  // Check for hidden/invisible styling
  const isHidden =
    /display:\s*none/gi.test(imgTag) ||
    /visibility:\s*hidden/gi.test(imgTag) ||
    /opacity:\s*0/gi.test(imgTag) ||
    /position:\s*absolute.*left:\s*-\d+/gi.test(imgTag);

  // Check for tracking-related URL patterns
  const hasTrackingPatterns =
    /(?:track|pixel|beacon|analytics|metric|stats|open|view)/i.test(src) ||
    /\.(gif|png|jpg|jpeg)\?.*(?:id|user|email|campaign|utm_)/i.test(src) ||
    /\/pixel\./i.test(src) ||
    /\/tracking\./i.test(src);

  // Check for common tracking domains
  const hasTrackingDomain =
    /(?:google-analytics|awstrack|googletagmanager|facebook|twitter|linkedin|mailchimp|constantcontact|sendgrid|mailgun)/i.test(
      src
    );

  return (
    has1x1Dimensions || isHidden || hasTrackingPatterns || hasTrackingDomain
  );
};

/**
 * Processes external images based on proxy configuration
 * @param content - HTML content to process
 * @param imageProxy - Image proxy configuration
 * @returns Object with processed content and image metadata for async loading
 */
export const processImages = (
  content: string,
  imageProxy?: ImageProxyConfig
): {
  processedContent: string;
  imagesToFetch: { originalSrc: string; placeholder: string }[];
} => {
  let processedContent = content;
  const imagesToFetch: { originalSrc: string; placeholder: string }[] = [];

  if (imageProxy?.enabled && imageProxy.baseUrl) {
    // Replace external images with placeholders for async loading
    processedContent = processedContent.replace(
      /<img([^>]*)src="(https?:\/\/[^"]*)"([^>]*)>/gi,
      (match, before, src, after) => {
        // Check if this is a tracking pixel - block these instead of proxying
        if (isLikelyTrackingPixel(match, src)) {
          return getBlockedImagePlaceholder();
        }

        // Create a unique placeholder ID for this image
        const placeholder = `proxy-image-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        imagesToFetch.push({ originalSrc: src, placeholder });

        return `<img${before} data-proxy-placeholder="${placeholder}"${after}>`;
      }
    );

    // Remove data URL images (potential malicious content)
    processedContent = processedContent.replace(
      /<img[^>]*src="data:[^"]*"[^>]*>/gi,
      getBlockedImagePlaceholder()
    );

    // Remove any remaining unhandled img tags (except our placeholders)
    processedContent = processedContent.replace(
      /<img(?![^>]*data-proxy-placeholder)[^>]*>/gi,
      getBlockedImagePlaceholder()
    );
  } else {
    // Original behavior - block all external images
    processedContent = processedContent
      // Remove external images (potential tracking pixels)
      .replace(
        /<img[^>]*src="https?:\/\/[^"]*"[^>]*>/gi,
        getBlockedImagePlaceholder()
      )
      // Remove data URL images (potential malicious content)
      .replace(
        /<img[^>]*src="data:[^"]*"[^>]*>/gi,
        getBlockedImagePlaceholder()
      )
      // Remove any remaining img tags
      .replace(/<img[^>]*>/gi, getBlockedImagePlaceholder());
  }

  return { processedContent, imagesToFetch };
};

/**
 * Sanitizes HTML content using DOMPurify with strict security configuration
 * @param htmlContent - Raw HTML content to sanitize
 * @param imageProxy - Image proxy configuration
 * @returns Object with sanitized content and images to fetch, or null if no content provided
 */
export const sanitizeEmailContent = (
  htmlContent?: string,
  imageProxy?: ImageProxyConfig
): {
  sanitizedHtml: string;
  imagesToFetch: { originalSrc: string; placeholder: string }[];
} | null => {
  if (!htmlContent) return null;

  const { processedContent, imagesToFetch } = processImages(
    htmlContent,
    imageProxy
  );

  const forbiddenTags = [
    'script',
    'object',
    'embed',
    'form',
    'input',
    'button',
  ];

  const forbiddenAttrs = [
    // Event handlers
    'onclick',
    'onload',
    'onerror',
    'onmouseover',
    'onmouseout',
    'onfocus',
    'onblur',
    'onchange',
    'onsubmit',
    'onreset',
    'onselect',
    'onunload',
    'onabort',
    'onkeydown',
    'onkeypress',
    'onkeyup',
    'onresize',
    'ondragdrop',
    'onmove',
    'onbounce',
    'onfinish',
    'onstart',
    'oncopy',
    'oncut',
    'onpaste',
    'onbeforeunload',
    'oncontextmenu',
    // Resource loading attributes
    'background',
    'href',
    'action',
    // Form attributes
    'formaction',
    'formmethod',
  ];

  // Configure img tag and attribute handling based on proxy settings
  if (!imageProxy?.enabled) {
    forbiddenTags.push('img');
    forbiddenAttrs.push('src');
    forbiddenAttrs.push('data-*');
  }

  let sanitized = DOMPurify.sanitize(processedContent, {
    // Remove all script tags and event handlers
    FORBID_TAGS: forbiddenTags,
    FORBID_ATTR: forbiddenAttrs,
    // Allow data attributes when proxy is enabled for our placeholder classes
    ALLOW_DATA_ATTR: imageProxy?.enabled || false,
    // Keep basic styling but remove dangerous CSS
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // Transform relative URLs to prevent navigation
    SANITIZE_DOM: true,
    WHOLE_DOCUMENT: false,
    RETURN_DOM_FRAGMENT: false,
  });

  // Additional post-processing to neutralize links
  sanitized = sanitized.replace(/<a\s+[^>]*href="[^"]*"[^>]*>/gi, (match) => {
    return match
      .replace(/href="[^"]*"/gi, 'href="#"')
      .replace(/target="[^"]*"/gi, '');
  });

  // Sanitize inline CSS to remove dangerous properties
  sanitized = sanitized.replace(/style="([^"]*)"/gi, (_, styleContent) => {
    const safeCss = styleContent
      .replace(/expression\s*\([^)]*\)/gi, '') // IE expression()
      .replace(/javascript\s*:/gi, '') // javascript: URLs
      .replace(/url\s*\([^)]*\)/gi, '') // All URL references
      .replace(/import\s*[^;]*/gi, '') // CSS imports
      .replace(/@[^;]*/gi, '') // At-rules like @import
      .replace(/behavior\s*:[^;]*/gi, '') // IE behavior
      .replace(/binding\s*:[^;]*/gi, '') // Mozilla binding
      .replace(/position\s*:\s*(fixed|absolute)/gi, '') // Dangerous positioning
      .replace(/z-index\s*:\s*\d+/gi, '') // Z-index manipulation
      // Keep only safe properties (colors, fonts, spacing, borders)
      .split(';')
      .filter((prop: string) => {
        const trimmed = prop.trim().toLowerCase();
        return (
          trimmed.match(
            /^(color|background-color|font|text|margin|padding|border|width|height|display|float|clear)/
          ) &&
          !trimmed.includes('url(') &&
          !trimmed.includes('expression(') &&
          !trimmed.includes('javascript:')
        );
      })
      .join(';');

    return safeCss ? `style="${safeCss}"` : '';
  });

  return { sanitizedHtml: sanitized, imagesToFetch };
};

/**
 * Creates a complete HTML document for iframe rendering
 * @param sanitizedHtml - Pre-sanitized HTML content
 * @param imageProxy - Image proxy configuration
 * @returns Complete HTML document string
 */
export const createIframeDocument = (
  sanitizedHtml: string,
  imageProxy?: ImageProxyConfig
): string => {
  // Adjust CSP and styles based on whether image proxy is enabled
  const imgCSP = imageProxy?.enabled
    ? "img-src 'self' data:;" // Allow self and data URLs for safe image content
    : "img-src 'none';";
  const imgStyle = imageProxy?.enabled
    ? ''
    : 'img { display: none !important; }';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; ${imgCSP} script-src 'none'; object-src 'none'; frame-src 'none'; worker-src 'none'; child-src 'none'; connect-src 'none'; font-src 'none'; media-src 'none'; manifest-src 'none'; base-uri 'none'; form-action 'none'; upgrade-insecure-requests;">
        <style>
          a {
            color: inherit;
            text-decoration: underline !important;
            cursor: default !important;
            pointer-events: none !important;
          }
          form, input, button, select, textarea {
            display: none !important;
          }
          /* Block any remaining images if proxy not enabled */
          ${imgStyle}
          /* Override any absolute positioning */
          * {
            position: static !important;
          }
          /* Prevent external resource loading via CSS */
          *[style*="background-image"] {
            background-image: none !important;
          }
        </style>
      </head>
      <body>
        ${sanitizedHtml}
      </body>
    </html>
  `;
};
